import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { MarkdownViewer } from '~/components/common/MarkdownViewer'
import useToast from '~/hooks/toast'
import { useUserStore } from '~/stores/userStore'
import type { Notification } from '~/types/activity.types'
import { getActivityIcon, getActivityText } from '~/utils/activity.utils'

type NotificationWSMessage = {
  type: 'notification'
  notification: Notification
}

const NotificationContext = createContext<null>(null)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUserStore()
  const queryClient = useQueryClient()
  const toast = useToast()
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const isExplicitClose = useRef(false)

  const connect = useCallback(() => {
    if (!user) {
      console.info('[NotificationWS] No user found, skipping connection.')
      return
    }

    // Construct WebSocket URL based on current window location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const wsUrl = `${protocol}//${host}/api/ws`

    // Close existing socket if any before creating a new one
    if (socketRef.current) {
      isExplicitClose.current = true
      socketRef.current.close()
    }

    console.info(`[NotificationWS] Connecting to ${wsUrl}...`)
    const ws = new WebSocket(wsUrl)
    socketRef.current = ws
    isExplicitClose.current = false

    ws.onopen = () => {
      console.info('[NotificationWS] Connected')
    }

    ws.onmessage = (event) => {
      try {
        const data: NotificationWSMessage = JSON.parse(event.data)
        if (data.type === 'notification') {
          console.info('[NotificationWS] New notification received:', data.notification)

          // 1. Invalidate notification queries to update UI counts/lists
          queryClient.invalidateQueries(['notifications'])
          queryClient.invalidateQueries(['comments'])

          // 2. Show toast
          toast?.current?.show({
            severity: 'info',
            summary: getActivityText(
              data.notification.activityType,
              data.notification.actor?.name || 'System',
            ),
            detail: (
              <MarkdownViewer
                content={data.notification.message || 'You have a new activity update'}
                limitLines={2}
                className='mt-1 text-sm'
              />
            ),
            icon: getActivityIcon(data.notification.activityType),
            life: 5000,
          })
        }
      } catch (err) {
        console.error('[NotificationWS] Failed to parse message:', err)
      }
    }

    ws.onclose = (event) => {
      console.info(`[NotificationWS] Disconnected (Code: ${event.code})`)
      socketRef.current = null

      // If we got a 401/403 equivalent (Policy Violation), don't auto-reconnect
      if (event.code === 1008) {
        console.warn(
          '[NotificationWS] Connection closed due to policy violation (Unauthenticated). Stopping reconnect.',
        )
        isExplicitClose.current = true
        return
      }

      // Reconnect if not explicitly closed and user is still logged in
      if (!isExplicitClose.current && user) {
        console.info('[NotificationWS] Attempting reconnect in 3s...')
        reconnectTimeoutRef.current = window.setTimeout(connect, 3000)
      }
    }

    ws.onerror = (err) => {
      console.error('[NotificationWS] WebSocket error:', err)
      // Standard close will trigger onclose which handles reconnect
      ws.close()
    }
  }, [user, queryClient, toast])

  useEffect(() => {
    if (user) {
      connect()
    } else {
      console.info('[NotificationWS] User logged out, cleaning up...')
      isExplicitClose.current = true
      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    return () => {
      isExplicitClose.current = true
      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [user, connect])

  return <NotificationContext.Provider value={null}>{children}</NotificationContext.Provider>
}

export const useNotificationWS = () => useContext(NotificationContext)
